// src/components/navigation/navigationConfig.ts
//
// Every menu item declares a `requires` clause so users only see what they
// can use. Items without permission gates (`requires` omitted) show for
// everyone — reserved for things like external links, logout, and
// account-self pages.
export const navigationConfig = {
  main: [
    {
      type: 'link',
      label: 'Layers',
      tool: 'layers',
      icon: 'fa-solid fa-layer-group',
      requires: { feature: 'layers', operation: 'read' },
    },
    {
      type: 'group',
      label: 'Tools',
      children: [
        {
          type: 'link',
          label: 'Duty Log Explorer',
          tool: 'dutylogs',
          icon: 'fa-solid fa-file-lines',
          requires: { feature: 'duty_logs', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Weather Data Explorer',
          tool: 'weatherData',
          icon: 'fa-solid fa-cloud-sun',
          requires: { feature: 'weather', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Values at Risk Explorer',
          tool: 'varExplorer',
          icon: 'fa-solid fa-triangle-exclamation',
          requires: { feature: 'var', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Wildfire Explorer',
          tool: 'wildfire',
          icon: 'fa-solid fa-fire-flame-curved',
          requires: { feature: 'wildfires', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Hotspot Explorer',
          tool: 'hotspotExplorer',
          icon: 'fa-solid fa-satellite-dish',
          requires: { feature: 'hotspots', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Lightning Explorer',
          tool: 'lightning',
          icon: 'fa-solid fa-bolt-lightning',
          requires: { feature: 'lightning', operation: 'read' },
        },
        {
          type: 'link',
          label: 'AFF Explorer',
          tool: 'aff',
          icon: 'fa-solid fa-plane',
          // aff.explore; aff.read is only the live layer. Literals: generate-navigation.js evals this object.
          requires: { feature: 'aff', operation: 'explore' },
        },
        {
          type: 'link',
          label: 'Weather Station Explorer',
          tool: 'weatherStations',
          icon: 'fa-solid fa-tower-broadcast',
          requires: { feature: 'stations', operation: 'read' },
        },
        {
          type: 'cross-app',
          label: 'Duty Roster',
          href: 'cms://roster',
          icon: 'fa-solid fa-clipboard-list',
          requires: { feature: 'cms', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Fire Modeling',
          tool: 'fireModeling',
          icon: 'fa-solid fa-fire',
          requires: { feature: 'modelling', operation: 'read' },
        },
        {
          type: 'link',
          label: 'Regional Fire Behaviour Forecast',
          tool: 'rfbf',
          icon: 'fa-solid fa-circle-exclamation',
          requires: { feature: 'tools', operation: 'rfbf' },
        },
        {
          type: 'link',
          label: 'Lat Long Converter',
          tool: 'latlong',
          icon: 'fa-solid fa-location-crosshairs',
          requires: { feature: 'tools', operation: 'lat_long' },
        },
        {
          type: 'link',
          label: 'Quick Zoom',
          tool: 'quickZoom',
          icon: 'fa-solid fa-magnifying-glass',
          requires: { feature: 'tools', operation: 'quick_zoom' },
        },
        // Consolidated into MapContextMenu (right-click)
        // { type: 'link', label: 'Placename Tool', tool: 'placenameTool' },
        {
          type: 'link',
          label: 'My Location',
          tool: 'myLocation',
          icon: 'fa-solid fa-location-dot',
          requires: { feature: 'tools', operation: 'my_location' },
        },
        // { type: 'link', label: 'Get Feature Info', tool: 'getFeatureInfo' },
        // { type: 'link', label: 'SpotWx Forecast', tool: 'spotWx' },
        {
          type: 'link',
          label: 'Download GIS Data',
          tool: 'gisDownload',
          icon: 'fa-solid fa-download',
          requires: { feature: 'gis', operation: 'read' },
        },
      ],
    },
    {
      type: 'link',
      label: 'Support',
      tool: 'support',
      icon: 'fa-solid fa-headset',
      requires: { feature: 'support', operation: 'create' },
    },
    {
      type: 'link',
      label: 'Save or print map',
      tool: 'printMap',
      icon: 'fa-solid fa-print',
      requires: { feature: 'tools', operation: 'print_map' },
    },
    {
      type: 'cross-app',
      label: 'Dashboard',
      href: 'cms://',
      icon: 'fa-solid fa-gauge',
      requires: { feature: 'cms', operation: 'read' },
    },
    {
      type: 'group',
      label: 'External Links',
      requires: { feature: 'external_links', operation: 'read' },
      children: [
        {
          type: 'external',
          label: 'GNWT Highway Conditions',
          href: 'https://www.dot.gov.nt.ca/Highways',
          icon: 'fa-solid fa-road',
        },
        {
          type: 'external',
          label: 'FMD Camera Network (Creds req)',
          href: 'https://sites.google.com/view/ecc-camera-sites/home',
          icon: 'fa-solid fa-camera',
        },
        {
          type: 'external',
          label: 'SOA documents (Corp only)',
          href: 'http://intranet.pws.gov.nt.ca/SSA/SOA_Listing.aspx',
          icon: 'fa-solid fa-file-contract',
        },
        {
          type: 'external',
          label: 'WBNP Fire Information',
          href: 'https://parks.canada.ca/pn-np/nt/woodbuffalo/nature/science_nature/fire_management/feu-foret-fire',
          icon: 'fa-solid fa-tree',
        },
        {
          type: 'external',
          label: 'Alberta Wildfire Status Dashboard',
          href: 'https://experience.arcgis.com/experience/0e45bd0ef9814d5e9ec3f87900a4cfe9',
          icon: 'fa-solid fa-map-location-dot',
        },
        {
          type: 'external',
          label: 'BC Wildfire Information Map',
          href: 'https://wildfiresituation.nrs.gov.bc.ca/map',
          icon: 'fa-solid fa-map-location-dot',
        },
        {
          type: 'external',
          label: 'Manitoba Fire Map',
          href: 'https://www.gov.mb.ca/conservation_fire/Fire-Maps/fireview/fireview_map.html',
          icon: 'fa-solid fa-map-location-dot',
        },
        {
          type: 'external',
          label: 'Saskatchewan Public Fire Map',
          href: 'https://gisappl.saskatchewan.ca/Html5Ext/?viewer=wfmpublic',
          icon: 'fa-solid fa-map-location-dot',
        },
        {
          type: 'external',
          label: 'NRCAN/CWFIS Interactive Map',
          href: 'https://cwfis.cfs.nrcan.gc.ca/interactive-map',
          icon: 'fa-solid fa-globe',
        },
      ],
    },
  ],
  account: [
    // Account self-management — every authenticated user sees these.
    { type: 'link', label: 'Account details', tool: 'userEdit', icon: 'fa-solid fa-user-pen' },
    { type: 'link', label: 'Password reset', tool: 'passwordReset', icon: 'fa-solid fa-key' },
    {
      type: 'link',
      label: 'Theme preferences',
      tool: 'themePreferences',
      icon: 'fa-solid fa-palette',
    },
    { type: 'link', label: 'Logout', tool: 'onLogout', icon: 'fa-solid fa-right-from-bracket' },
  ],
  // Admin-only menu items. Each declares its own `requires` so removing
  // a permission from `admin` automatically hides the corresponding link.
  admin: [
    {
      type: 'link',
      label: 'Layer Admin',
      tool: 'layerAdmin',
      icon: 'fa-solid fa-layer-group',
      requires: { feature: 'layer_manager', operation: 'create' },
    },
    {
      type: 'link',
      label: 'Audit Log',
      tool: 'auditExplorer',
      icon: 'fa-solid fa-clipboard-list',
      requires: { feature: 'audit_log', operation: 'read' },
    },
    {
      type: 'link',
      label: 'Permissions',
      tool: 'permissionManager',
      icon: 'fa-solid fa-shield-halved',
      requires: { feature: 'audit_log', operation: 'read' },
    },
    {
      type: 'link',
      label: 'Re-Issue Reports',
      tool: 'reIssueReports',
      icon: 'fa-solid fa-file-export',
      requires: { feature: 'reports', operation: 'reissue' },
    },
  ],
} as const;
